/**
 * QAISignupPage (F2) - v2.0 Redesigned
 * Google OAuth signup page for Q AI standalone users
 *
 * Route: /q-ai/signup (public)
 * Target: New retail MT5 traders signing up for Q AI
 *
 * v2.0 Changes:
 * - Consistent branding with landing page
 * - Enhanced visual design
 * - Trust indicators and benefits
 * - Better user experience
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useGoogleLogin } from '@react-oauth/google';
import QAILogo from '../../Components/QAI/QAILogo';
import { googleLogin } from '../../api/qaiApi';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS (Matching Landing Page)
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
  primary: '#27B56D',
  primaryLight: '#22C55E',
  primaryDark: '#16A34A',
  background: '#0A0A0F',
  surface: '#12121A',
  text: '#FFFFFF',
  textSecondary: '#E2E8F0',
  textMuted: '#94A3B8',
  textDim: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.12)',
  glow: 'rgba(39, 181, 109, 0.4)',
  error: '#EF4444'
};

// ═══════════════════════════════════════════════════════════════════════════════
// BENEFITS LIST
// ═══════════════════════════════════════════════════════════════════════════════

const SIGNUP_BENEFITS = [
  'Get 50 free tokens to start',
  'No credit card required',
  'Instant access to Q AI Coach'
];

/**
 * QAISignupPage Component
 */
const QAISignupPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Google OAuth login hook
  const googleOAuth = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        setError(null);

        // Get user info from Google using the access token
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });
        const userInfo = await userInfoResponse.json();

        // Send to backend for authentication/registration
        const { token, user } = await googleLogin(tokenResponse.access_token, 'qai');

        // Store Q AI specific tokens
        localStorage.setItem('qai_token', token);
        localStorage.setItem('qai_user', JSON.stringify(user));

        // Navigate to coach page
        navigate('/q-ai/coach');
      } catch (err) {
        console.error('Google signup error:', err);
        setError(err.message || 'Signup failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Google OAuth error:', errorResponse);
      setError('Google sign-in failed. Please try again.');
      setIsLoading(false);
    },
    flow: 'implicit'
  });

  // Handle Google signup button click
  const handleGoogleSignup = () => {
    setError(null);
    setIsLoading(true);
    googleOAuth();
  };

  return (
    <Box
      sx={{
        backgroundColor: BRAND.background,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Glow Effect */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '400px',
          background: `radial-gradient(ellipse, rgba(39, 181, 109, 0.08) 0%, transparent 60%)`,
          pointerEvents: 'none'
        }}
      />

      {/* Back Navigation */}
      <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/q-ai')}
          sx={{
            color: BRAND.textMuted,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              color: BRAND.text,
              backgroundColor: 'transparent'
            }
          }}
        >
          Back to Q AI
        </Button>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="sm"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            textAlign: 'center'
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <QAILogo size="large" animated />
          </Box>

          {/* Title */}
          <Typography
            variant="h1"
            sx={{
              color: BRAND.text,
              fontSize: { xs: '1.75rem', md: '2rem' },
              fontWeight: 800,
              mb: 1,
              letterSpacing: '-0.02em'
            }}
          >
            Create Your Q AI Account
          </Typography>

          <Typography
            sx={{
              color: BRAND.textSecondary,
              fontSize: '1rem',
              mb: 4
            }}
          >
            Start trading smarter with AI-powered insights
          </Typography>

          {/* Benefits */}
          <Box
            sx={{
              backgroundColor: BRAND.surface,
              borderRadius: '16px',
              border: `1px solid ${BRAND.border}`,
              p: 3,
              mb: 4
            }}
          >
            {SIGNUP_BENEFITS.map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: index < SIGNUP_BENEFITS.length - 1 ? 1.5 : 0
                }}
              >
                <CheckCircleIcon sx={{ color: BRAND.primary, fontSize: 20 }} />
                <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.9375rem' }}>
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: BRAND.error,
                borderRadius: '12px',
                border: `1px solid rgba(239, 68, 68, 0.2)`,
                '& .MuiAlert-icon': { color: BRAND.error }
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Google Signup Button */}
          <Button
            fullWidth
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : (
              <Box
                component="img"
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                sx={{ width: 20, height: 20 }}
              />
            )}
            onClick={handleGoogleSignup}
            disabled={isLoading}
            sx={{
              py: 1.75,
              backgroundColor: BRAND.text,
              color: '#000',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '12px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#f0f0f0',
                boxShadow: `0 0 20px rgba(255, 255, 255, 0.2)`
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(0, 0, 0, 0.5)'
              }
            }}
          >
            {isLoading ? 'Creating account...' : 'Continue with Google'}
          </Button>

          {/* Divider */}
          <Divider
            sx={{
              my: 4,
              '&::before, &::after': {
                borderColor: BRAND.border
              }
            }}
          >
            <Typography sx={{ color: BRAND.textDim, fontSize: '0.8125rem', px: 2 }}>
              or
            </Typography>
          </Divider>

          {/* Platform Login Option */}
          <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem', mb: 2 }}>
            Already a QTradeView platform user?
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{
              py: 1.5,
              borderColor: BRAND.borderLight,
              color: BRAND.text,
              textTransform: 'none',
              fontSize: '0.9375rem',
              fontWeight: 500,
              borderRadius: '12px',
              '&:hover': {
                borderColor: BRAND.primary,
                backgroundColor: 'rgba(39, 181, 109, 0.1)'
              }
            }}
          >
            Login with Platform Account
          </Button>

          {/* Terms */}
          <Typography
            sx={{
              color: BRAND.textDim,
              fontSize: '0.75rem',
              mt: 4,
              lineHeight: 1.7
            }}
          >
            By signing up, you agree to our{' '}
            <Box
              component="span"
              sx={{
                color: BRAND.primary,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Terms of Service
            </Box>{' '}
            and{' '}
            <Box
              component="span"
              sx={{
                color: BRAND.primary,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Privacy Policy
            </Box>
          </Typography>

          {/* Login Link */}
          <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem', mt: 4 }}>
            Already have a Q AI account?{' '}
            <Box
              component="span"
              onClick={() => navigate('/q-ai/login')}
              sx={{
                color: BRAND.primary,
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Log in
            </Box>
          </Typography>
        </Box>
      </Container>

      {/* Footer Trust Indicators */}
      <Box
        sx={{
          py: 3,
          borderTop: `1px solid ${BRAND.border}`,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 3, md: 5 },
              flexWrap: 'wrap'
            }}
          >
            {[
              { icon: <SecurityIcon sx={{ fontSize: 18 }} />, text: 'Bank-Grade Security' },
              { icon: <SpeedIcon sx={{ fontSize: 18 }} />, text: 'Instant Setup' },
              { icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />, text: 'Powered by Claude' }
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: BRAND.textDim
                }}
              >
                <Box sx={{ color: BRAND.primary, display: 'flex' }}>
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default QAISignupPage;
